/**
 * Recuperação de senha e confirmação de cadastro por e-mail (US-201 · RF-09) — Nexora Grid (frontend).
 *
 * Criado em: 18/10/2026 · Última modificação: 18/10/2026 · Versão: 1.0.0
 *
 * - `RecuperarSenha`: pede o link de redefinição pelo login ou e-mail. A resposta é sempre a mesma (não revela se a
 *   conta existe);
 * - `DefinirSenha`: páginas públicas `/redefinir-senha` e `/confirmar-cadastro`, abertas pelo link do e-mail
 *   (`?token=`). A senha segue a política da plataforma (mínimo de 12 caracteres, conferida no backend).
 */
import { useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { TituloMarca } from "../marca";

export function RecuperarSenha({ voltar }: { voltar: () => void }) {
  const [valor, setValor] = useState("");
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [esperando, setEsperando] = useState(false);
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    setEsperando(true);
    try {
      const r = await api<{ mensagem: string }>("/v1/auth/recuperar-senha", { metodo: "POST", corpo: { login_ou_email: valor.trim() } });
      setMensagem(r.mensagem);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha.message : "Backend indisponível.");
    } finally {
      setEsperando(false);
    }
  };
  return (
    <form onSubmit={enviar} aria-label="Recuperar senha" className="form-conta">
      <h2>Esqueci minha senha</h2>
      {mensagem ? <p role="status">{mensagem}</p> : (
        <>
          <p className="nx-suave">Informe o seu login ou o e-mail cadastrado. Enviaremos um link para criar uma nova senha.</p>
          <div className="nx-campo" data-obrigatorio>
            <label htmlFor="login-ou-email">Login ou e-mail</label>
            <input id="login-ou-email" autoComplete="username" value={valor} onChange={(e) => setValor(e.target.value)} required minLength={3} />
          </div>
          {erro && <p className="erro" role="alert">{erro}</p>}
          <button type="submit" className="nx-botao nx-botao-principal" disabled={esperando}>{esperando ? "Enviando…" : "Enviar link"}</button>
        </>
      )}
      <button type="button" className="nx-botao" onClick={voltar}>Voltar ao login</button>
    </form>
  );
}

export function DefinirSenha({ tipo }: { tipo: "recuperacao" | "confirmacao" }) {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [senha, setSenha] = useState("");
  const [repetida, setRepetida] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<string | null>(null);
  const [esperando, setEsperando] = useState(false);
  const titulo = tipo === "confirmacao" ? "Confirme seu cadastro" : "Nova senha";
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    if (senha !== repetida) { setErro("As senhas não conferem."); return; }
    setEsperando(true);
    try {
      const rota = tipo === "confirmacao" ? "/v1/auth/confirmar-cadastro" : "/v1/auth/redefinir-senha";
      const r = await api<{ login: string; mensagem: string }>(rota, { metodo: "POST", corpo: { token, senha } });
      setFeito(`${r.mensagem} (login: ${r.login})`);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha.message : "Backend indisponível.");
    } finally {
      setEsperando(false);
    }
  };
  return (
    <div className="nx-tela tela-login" data-tema="cco">
      <form className="nx-cartao cartao-login form-conta" onSubmit={enviar} aria-label={titulo}>
        <TituloMarca />
        <h2>{titulo}</h2>
        {feito ? (
          <p role="status">{feito}</p>
        ) : !token ? (
          <p className="erro" role="alert">Link incompleto: abra o endereço exatamente como veio no e-mail.</p>
        ) : (
          <>
            <p className="nx-suave">Mínimo de 12 caracteres; evite senhas comuns e o próprio login.</p>
            <div className="nx-campo" data-obrigatorio>
              <label htmlFor="nova-senha">Nova senha</label>
              <input id="nova-senha" type="password" autoComplete="new-password" value={senha} onChange={(e) => setSenha(e.target.value)} required minLength={12} />
            </div>
            <div className="nx-campo" data-obrigatorio>
              <label htmlFor="repetir-senha">Repita a senha</label>
              <input id="repetir-senha" type="password" autoComplete="new-password" value={repetida} onChange={(e) => setRepetida(e.target.value)} required minLength={12} />
            </div>
            {erro && <p className="erro" role="alert">{erro}</p>}
            <button type="submit" className="nx-botao nx-botao-principal" disabled={esperando}>{esperando ? "Salvando…" : "Definir senha"}</button>
          </>
        )}
        <a className="nx-botao" href="/">Ir para o login</a>
      </form>
    </div>
  );
}
